import { Injectable, signal } from '@angular/core';
import { TopLevelRoutes } from '@r1-compass-library/models';

/**
 * The set of valid feature keys for the sidenav validation registry.
 * Derived directly from `TopLevelRoutes` so that adding a new top-level route
 * automatically makes its path a valid key — no separate list to maintain.
 */
export type SidenavFeatureKey = (typeof TopLevelRoutes)[number]['path'];

/**
 * Record of the sidenav domain section keys to invalid sections
 */
type Registry = Record<SidenavFeatureKey, string[]>;

/**
 * Central registry that connects feature validation state to the sidenav.
 *
 * Each feature that has section-level validation indicators in the sidenav registers its
 * `invalidSections` signal here. `CompassComponent` reads from this registry to decorate
 * sidenav links with `hasValidationIssue` — it never needs to know about individual
 * feature validation state services.
 *
 * ---
 *
 * ### How to hook up a new feature
 *
 * **1. Create a `ValidationStateService` for your feature** ({@link ValidationStateService} for details).
 *
 * **2. Register in your feature's parent component `constructor`:**
 * ```typescript
 * constructor() {
 *     const myFeatureValidationStateService = inject(MyFeatureValidationStateService)
 *     const activatedRoute = inject(ActivatedRoute);
 *     const sidenavValidationRegistry = inject(SidenavValidationRegistry);
 *
 *     effect(
 *         () => {
 *             const routeData = activatedRoute.snapshot.data as CompassRouteParent;
 *             const invalidSections = myFeatureValidationStateService.invalidSections();
 *
 *             sidenavValidationRegistry.register(routeData.path, invalidSections);
 *         },
 *         { debugName: "Register feat's `invalidSections` under given feature key" }
 *     );
 * }
 * ```
 * Using `routeData.path` instead of a string literal means the key is always in sync with
 * the route definition and is type-checked against `SidenavFeatureKey`.
 *
 * **3. Ensure your feature's sidenav links have `sectionKey` set when the link `url` does not
 *    match the validation section name.** If `link.url === sectionName`, no `sectionKey` is needed.
 *    Only set `sectionKey` as an override when they differ (e.g. `url: 'builder'` but section is
 *    `'recordSelection'`). See `AppConstants` for examples.
 *
 * **4. `CompassComponent` reads `getInvalidSections(featureKey)` in its `links()` computed signal**
 *    and maps the result onto each link's `hasValidationIssue` property — no changes to
 *    `CompassComponent` are needed when adding a new feature.
 */
@Injectable({ providedIn: 'root' })
export class SidenavValidationRegistry {
    private readonly registry = signal<Registry>({} as Registry);

    /**
     * Registers a feature's `invalidSections` signal under the given feature key.
     * Call this once from the feature's parent component.
     *
     * @param featureKey      - The top-level route path for the feature (e.g. `'content-library'`).
     * @param invalidSections - The `invalidSections` signal from the feature's `ValidationStateService`.
     */
    public register(featureKey: SidenavFeatureKey, invalidSections: string[]): void {
        this.registry.update(registry => ({ ...registry, [featureKey]: invalidSections }));
    }

    /**
     * Returns a signal that emits the list of invalid section names for the given feature.
     * Emits an empty array if the feature has not yet registered or has no invalid sections.
     *
     * @param featureKey - The top-level route path for the feature.
     * @param registry - This service's registry of domain sectiosn to invalid subsections
     */
    public getInvalidSections(featureKey: SidenavFeatureKey): string[] {
        return this.registry()[featureKey] ?? [];
    }
}
